/**
 * ChatWindow Component
 * Real-time chat interface with per-message billing
 * Includes AI-powered chat summary feature
 * No free tier — every seeker message is charged
 */

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from "react-i18next";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, deleteField, getDocs, writeBatch, limit, startAfter } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useChatEncryption } from '../../lib/encryption/useChatEncryption';
import { blockSeeker, isBlockedByMaven } from '../../lib/blockService';
import { submitReport } from '../../lib/reportService';
import TranslatableMessage from './TranslatableMessage';

export default function ChatWindow({ chatId, userId, userRole, onClose }) {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  // Viewer's language for on-device chat auto-translation (Chrome built-in).
  // Reader's real language for chat auto-translation. Prefer the BROWSER language
  // (any language, e.g. vi/pt) over the app UI language (only EN/ES on Camyvera).
  const viewerLang = ((typeof navigator !== 'undefined' && navigator.language) || i18n?.language || 'en').split('-')[0];
  const [messages, setMessages] = useState([]);
  const [rawMessages, setRawMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [chatInfo, setChatInfo] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // E2E Encryption
  const encryption = useChatEncryption(chatId);
  const [encryptionInitialized, setEncryptionInitialized] = useState(false);

  // File attachment state
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Summary state
  const [showSummary, setShowSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const prevMessageCountRef = useRef(0);
  const isUserScrollingRef = useRef(false);
  const messagesContainerRef = useRef(null);
  const decryptedCacheRef = useRef(new Map());
  const isDeletedForMe = (msg) => Boolean(msg?.deletedFor && userId && msg.deletedFor[userId]);

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  };

  useEffect(() => {
    if (!messages.length) return;
    const grew = messages.length > prevMessageCountRef.current;
    const isInitial = prevMessageCountRef.current === 0;
    prevMessageCountRef.current = messages.length;

    const el = messagesContainerRef.current;
    // Deterministic "go to bottom" — set scrollTop directly rather than a smooth
    // scrollIntoView that starts before content settles and stops mid-list.
    const jump = () => {
      if (el) el.scrollTop = el.scrollHeight;
      else messagesEndRef.current?.scrollIntoView({ block: 'end' });
    };

    // First render of a chat: ALWAYS land at the newest message, with a few retries
    // because messages/decrypted text/images render slightly later (that late layout
    // shift is what used to leave the view "in the middle"). Ignore the user-scroll
    // flag here — the user hasn't had a chance to scroll on the first paint.
    if (isInitial) {
      requestAnimationFrame(jump);
      [80, 200, 400, 700].forEach((t) => setTimeout(jump, t));
      return;
    }

    // New incoming/outgoing message: keep pinned to the bottom unless the user has
    // deliberately scrolled up to read history.
    if (grew && !isUserScrollingRef.current) {
      scrollToBottom('smooth');
    }
  }, [messages]);

  useEffect(() => {
    try {
      const existing = window.localStorage.getItem('cmv_chat_device_id');
      if (existing) {
        setDeviceId(existing);
        return;
      }
      const generated = `device_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem('cmv_chat_device_id', generated);
      setDeviceId(generated);
    } catch (error) {
      console.warn('[Chat] Failed to init device id:', error);
    }
  }, []);

  useEffect(() => {
    decryptedCacheRef.current.clear();
    setEncryptionInitialized(false);
    setRawMessages([]);
    setMessages([]);
    setChatInfo(null);
    prevMessageCountRef.current = 0; // so the next chat jumps to its own bottom
    isUserScrollingRef.current = false;
  }, [chatId]);

  const decryptMessages = async (msgs) => {
    if (!deviceId) {
      setMessages(msgs.filter((msg) => !isDeletedForMe(msg)));
      return;
    }
    if (!encryption.publicKey) {
      setMessages(msgs.filter((msg) => !isDeletedForMe(msg)));
      return;
    }
    if (!encryption.state.hasSharedKey && !chatInfo?.encryptionPublicKeys) {
      const marked = msgs.filter((msg) => !isDeletedForMe(msg)).map((msg) => {
        if (msg.isEncrypted && !msg.text) {
          return { ...msg, text: t("chat.window.encryptedMessage", "🔒 Encrypted message") };
        }
        return msg;
      });
      setMessages(marked);
      return;
    }

    const decryptedMsgs = await Promise.all(
      msgs.filter((msg) => !isDeletedForMe(msg)).map(async (msg) => {
        if (msg.isEncrypted && (msg.encrypted || msg.encryptedFor)) {
          const cached = decryptedCacheRef.current.get(msg.id);
          if (cached) return { ...msg, text: cached };

          try {
            const senderKeys = chatInfo?.encryptionPublicKeys?.[msg.senderId];
            const encryptedPayload = msg.encryptedFor && deviceId
              ? msg.encryptedFor[deviceId]
              : msg.encrypted;
            if (!encryptedPayload) return { ...msg, text: t("chat.window.encryptedMessage", "🔒 Encrypted message") };
            if (!encryptedPayload.version || encryptedPayload.version < 5) return { ...msg, text: t("chat.window.encryptedMessage", "🔒 Encrypted message") };

            let allSenderKeys = typeof senderKeys === 'string'
              ? [senderKeys]
              : (senderKeys ? Object.values(senderKeys) : []);
            if (msg.senderId === userId && encryption.publicKey && !allSenderKeys.includes(encryption.publicKey)) {
              allSenderKeys = [encryption.publicKey, ...allSenderKeys];
            }
            if (allSenderKeys.length === 0) return { ...msg, text: t("chat.window.encryptedMessage", "🔒 Encrypted message") };

            const result = await encryption.decryptFromPeerWithFallback(allSenderKeys, encryptedPayload);
            if (!result.success) return { ...msg, text: t("chat.window.encryptedMessage", "🔒 Encrypted message") };

            decryptedCacheRef.current.set(msg.id, result.text);
            return { ...msg, text: result.text };
          } catch (err) {
            return { ...msg, text: t("chat.window.encryptedMessageFallback", "[Encrypted message]") };
          }
        }
        return msg;
      })
    );
    setMessages(decryptedMsgs);
  };

  const deleteMessageForMe = async (msg) => {
    if (!chatId || !userId || !msg?.id) return;
    try {
      const msgRef = doc(db, 'chats', chatId, 'messages', msg.id);
      await updateDoc(msgRef, {
        [`deletedFor.${userId}`]: true,
        deletedAt: serverTimestamp(),
        deletedBy: userId,
      });
      decryptedCacheRef.current.delete(msg.id);
      setRawMessages((prev) => prev.filter((m) => m.id !== msg.id));
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    } catch (err) {
      console.error('Failed to delete message:', err);
      setError(t("chat.window.failedToDeleteMessage", "Failed to delete message"));
    }
  };

  const reportMessage = async (msg) => {
    if (!userId || !msg?.id) return;
    if (msg.senderId === userId) return;
    const ok = window.confirm(
      t("chat.window.reportMessageConfirm", "Report this message?\n\nOur team reviews reports within 24 hours and removes content or accounts that violate our guidelines.")
    );
    if (!ok) return;
    const targetId = msg.senderId;
    const senderIsExpert = chatInfo?.expertId === targetId;
    const targetType = senderIsExpert ? 'maven' : 'seeker';
    const targetName = senderIsExpert ? chatInfo?.expertName : chatInfo?.questerName;
    try {
      await submitReport({
        type: 'chat_message',
        targetType,
        targetId,
        targetName: targetName || null,
        reporterId: userId,
        reporterRole: userRole || null,
        contentRef: {
          chatId,
          messageId: msg.id,
          messageText: msg.text || '',
        },
      });
      alert(t("chat.window.reportSubmitted", "Report submitted. Thank you — our team will review this within 24 hours."));
    } catch (err) {
      console.error('Failed to submit report:', err);
      alert(err?.message || t("chat.window.couldNotSubmitReport", "Could not submit report. Please try again."));
    }
  };

  const clearChatHistoryForMe = async () => {
    if (!chatId || !userId) return;
    const confirmClear = window.confirm(t("chat.window.deleteConversationConfirm", "Delete this conversation? It will be removed from your chat list."));
    if (!confirmClear) return;
    try {
      // Mark the chat itself as deleted for this user
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`deletedFor.${userId}`]: true,
        [`deletedAt_${userId}`]: serverTimestamp(),
      });

      // Also mark all messages as deleted for this user
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      let lastDoc = null;
      while (true) {
        const q = lastDoc
          ? query(messagesRef, orderBy('createdAt', 'asc'), startAfter(lastDoc), limit(200))
          : query(messagesRef, orderBy('createdAt', 'asc'), limit(200));
        const snap = await getDocs(q);
        if (snap.empty) break;
        const batch = writeBatch(db);
        snap.docs.forEach((docSnap) => {
          batch.update(docSnap.ref, {
            [`deletedFor.${userId}`]: true,
            deletedAt: serverTimestamp(),
            deletedBy: userId,
          });
        });
        await batch.commit();
        lastDoc = snap.docs[snap.docs.length - 1];
      }
      decryptedCacheRef.current.clear();
      setRawMessages([]);
      setMessages([]);
      // Close the chat window after deletion
      if (onClose) onClose();
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      setError(t("chat.window.failedToDeleteConversation", "Failed to delete conversation"));
    }
  };

  const markAsRead = async () => {
    if (!chatId || !userId) return;
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`lastReadAt_${userId}`]: serverTimestamp(),
        [`unreadCount_${userId}`]: 0
      });
    } catch (err) {
      console.error('Error marking chat as read:', err);
    }
  };

  useEffect(() => {
    if (chatId && userId && messages.length > 0) {
      markAsRead();
    }
  }, [chatId, userId, messages.length]);

  // Subscribe to real-time messages
  // Messages listener — subscribe ONCE per chat. Decryption re-runs via the
  // dedicated effect below when device/encryption state changes, so we never
  // re-subscribe this listener (which would re-read the whole message list and
  // pile up listeners → Firestore resource-exhausted).
  useEffect(() => {
    if (!chatId) return;
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    const unsubscribeMessages = onSnapshot(q, async (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
      setRawMessages(msgs);
      try {
        await decryptMessages(msgs);
      } catch (e) {
        setMessages(msgs.filter((m) => !isDeletedForMe(m)));
      }
    }, (err) => {
      console.error('[ChatWindow] messages listener error:', err?.code, err?.message);
      setError(err?.code === 'resource-exhausted'
        ? t("chat.window.rateLimited", "Chat is temporarily rate-limited — please retry in a moment.")
        : t("chat.window.couldNotLoadMessages", "Could not load messages: {{reason}}", { reason: err?.code || err?.message || 'unknown' }));
    });
    return () => unsubscribeMessages();
  }, [chatId]);

  // Chat metadata + encryption init. Bounded re-runs (chat/device/init), NOT on
  // every chat-doc update — avoids listener churn.
  useEffect(() => {
    if (!chatId) return;
    const chatRef = doc(db, 'chats', chatId);
    const unsubscribeChat = onSnapshot(chatRef, async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();

        // Fetch current expert rate from their profile
        let currentExpertRate = data.expertRate || 1.00;
        try {
          const expertDoc = await import('firebase/firestore').then(({ getDoc, doc: docRef }) =>
            getDoc(docRef(db, 'users', data.expertId))
          );
          if (expertDoc.exists()) {
            const expertData = expertDoc.data();
            currentExpertRate = expertData.rate || expertData.ratePerMinute || data.expertRate || 1.00;
          }
        } catch (err) {
          console.error('Error fetching expert rate:', err);
        }

        const peerId = userId === data.questerId ? data.expertId : data.questerId;
        let peerPublicKeys = data.encryption?.publicKeys?.[peerId] || null;
        // First-message fallback: if the peer hasn't published keys to THIS chat
        // yet (e.g. they've never opened it), look up their USER-LEVEL keys so we
        // can still encrypt the very first message instead of sending plaintext.
        // Seed them into the chat doc so the rest of the flow works as usual.
        if (!peerPublicKeys && peerId) {
          try {
            const { getDoc, doc: docRef } = await import('firebase/firestore');
            const peerUserSnap = await getDoc(docRef(db, 'users', peerId));
            const userLevel = peerUserSnap.exists() ? peerUserSnap.data()?.encryptionPublicKeys : null;
            if (userLevel && typeof userLevel === 'object' && Object.keys(userLevel).length > 0) {
              peerPublicKeys = userLevel;
              await updateDoc(chatRef, {
                [`encryption.publicKeys.${peerId}`]: userLevel,
                'encryption.enabled': true,
                'encryption.version': 5,
              }).catch(() => {});
            }
          } catch (fallbackErr) {
            // non-fatal — behaves exactly as before (plaintext) if lookup fails
          }
        }
        const peerPublicKey = typeof peerPublicKeys === 'string'
          ? peerPublicKeys
          : (peerPublicKeys ? Object.values(peerPublicKeys)[0] : null);
        const encryptionEnabled = data.encryption?.enabled || false;

        setChatInfo({
          ...data,
          expertRate: currentExpertRate,
          encryptionEnabled,
          peerPublicKey,
          peerPublicKeys: peerPublicKeys && typeof peerPublicKeys === 'object' ? peerPublicKeys : undefined,
          encryptionPublicKeys: data.encryption?.publicKeys || {}
        });

        if (encryption.isAvailable && !encryptionInitialized && deviceId) {
          try {
            const myPublicKey = await encryption.initializeEncryption(chatId, peerPublicKey);
            if (myPublicKey && userId) {
              const myKeys = data.encryption?.publicKeys?.[userId];
              const isLegacyKey = typeof myKeys === 'string';
              const existingKey = typeof myKeys === 'string'
                ? myKeys
                : (myKeys ? myKeys[deviceId] : null);
              if (!existingKey || existingKey !== myPublicKey) {
                if (isLegacyKey) {
                  await updateDoc(chatRef, {
                    [`encryption.publicKeys.${userId}`]: {
                      legacy: myKeys,
                      [deviceId]: myPublicKey,
                    },
                    'encryption.enabled': true,
                    'encryption.version': 5,
                  });
                } else {
                  await updateDoc(chatRef, {
                    [`encryption.publicKeys.${userId}.${deviceId}`]: myPublicKey,
                    'encryption.enabled': true,
                    'encryption.version': 5,
                  });
                }
              } else if (!isLegacyKey && myKeys && !myKeys.legacy) {
                await updateDoc(chatRef, {
                  [`encryption.publicKeys.${userId}.legacy`]: myPublicKey,
                  'encryption.enabled': true,
                  'encryption.version': 5,
                });
              }

              // Also publish this device's key at the USER level so a sender can
              // encrypt the FIRST message to me before I've opened their chat
              // (closes the plaintext-first-message gap). Chat-level keys above
              // remain authoritative per-chat; this is just a discovery registry.
              try {
                await updateDoc(doc(db, 'users', userId), {
                  [`encryptionPublicKeys.${deviceId}`]: myPublicKey,
                  encryptionKeyVersion: 5,
                  encryptionKeyUpdatedAt: serverTimestamp(),
                });
              } catch (userKeyErr) {
                console.warn('[Chat] Failed to publish user-level public key (non-fatal):', userKeyErr);
              }
            }
            setEncryptionInitialized(true);
          } catch (err) {
            console.error('[Chat] Failed to initialize encryption:', err);
          }
        }

        if (peerPublicKey && encryption.state.isInitialized && !encryption.state.hasSharedKey) {
          try {
            await encryption.setPeerPublicKey(peerPublicKey);
          } catch (err) {
            console.error('[Chat] Failed to set peer public key:', err);
          }
        }
      }
    });

    return () => unsubscribeChat();
  }, [chatId, deviceId, encryptionInitialized]);

  useEffect(() => {
    if (!rawMessages.length) return;
    decryptMessages(rawMessages);
  }, [encryption.state.hasSharedKey, rawMessages.length, chatInfo?.encryptionPublicKeys, deviceId]);

  useEffect(() => {
    if (!chatInfo?.peerPublicKey) return;
    if (!encryptionInitialized || encryption.state.hasSharedKey) return;
    encryption.setPeerPublicKey(chatInfo.peerPublicKey).catch((err) => {
      console.error('[Chat] Failed to set peer public key:', err);
    });
  }, [chatInfo?.peerPublicKey, encryptionInitialized, encryption.state.hasSharedKey]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError(t("chat.window.fileTooLarge", "File too large. Maximum size is 10MB."));
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const uploadAndSendFile = async () => {
    if (!selectedFile || uploading) return;
    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      let encryptedKeyForPayload = null;

      if (encryption.isAvailable) {
        if (!encryption.state.isInitialized || !encryption.publicKey) {
          await encryption.initializeEncryption(chatId, chatInfo?.peerPublicKey || null);
        }
      }

      let peerKeys = chatInfo?.peerPublicKeys || {};
      let selfKeys = chatInfo?.encryptionPublicKeys?.[userId] || {};
      const peerId = isQuester ? chatInfo?.expertId : chatInfo?.questerId;
      if (peerId) {
        try {
          const { getDoc, doc: docRef } = await import('firebase/firestore');
          const chatDoc = await getDoc(docRef(db, 'chats', chatId));
          if (chatDoc.exists()) {
            const allEncryptionKeys = chatDoc.data()?.encryption?.publicKeys;
            const freshPeerKeys = allEncryptionKeys?.[peerId];
            const freshSelfKeys = allEncryptionKeys?.[userId];
            if (freshPeerKeys && typeof freshPeerKeys === 'object') {
              peerKeys = freshPeerKeys;
            } else if (typeof freshPeerKeys === 'string') {
              peerKeys = { default: freshPeerKeys };
            }
            if (freshSelfKeys && typeof freshSelfKeys === 'object') {
              selfKeys = freshSelfKeys;
            }
          }
        } catch (err) {
          // Fallback to cached peer keys
        }
      }

      const peerPublicKey = chatInfo?.peerPublicKey || Object.values(peerKeys)[0] || null;
      if (!encryption.state.hasSharedKey && peerPublicKey) {
        await encryption.setPeerPublicKey(peerPublicKey);
      }
      if (encryption.state.hasSharedKey || peerPublicKey) {
        const fileBuffer = await selectedFile.arrayBuffer();
        const fileKeyBytes = new Uint8Array(32);
        if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
          window.crypto.getRandomValues(fileKeyBytes);
        }
        const fileKeyBase64 = btoa(String.fromCharCode(...fileKeyBytes));
        const encryptedKeyFor = {};
        const peerEntries = Object.entries(peerKeys);
        if (peerEntries.length > 0) {
          for (const [peerDeviceId, peerKey] of peerEntries) {
            const encryptedKey = await encryption.encryptForPeer(peerKey, fileKeyBase64);
            if (encryptedKey) encryptedKeyFor[peerDeviceId] = encryptedKey;
          }
        } else if (peerPublicKey) {
          const encryptedKey = await encryption.encryptForPeer(peerPublicKey, fileKeyBase64);
          if (encryptedKey) encryptedKeyFor.default = encryptedKey;
        }
        const selfEntries = typeof selfKeys === 'object' ? Object.entries(selfKeys) : [];
        if (selfEntries.length > 0) {
          for (const [selfDeviceId, selfKey] of selfEntries) {
            if (encryptedKeyFor[selfDeviceId]) continue;
            const encryptedSelfKey = await encryption.encryptForPeer(selfKey, fileKeyBase64);
            if (encryptedSelfKey) encryptedKeyFor[selfDeviceId] = encryptedSelfKey;
          }
        }
        if (deviceId && encryption.publicKey) {
          if (!encryptedKeyFor[deviceId]) {
            const encryptedSelfKey = await encryption.encryptForPeer(encryption.publicKey, fileKeyBase64);
            if (encryptedSelfKey) encryptedKeyFor[deviceId] = encryptedSelfKey;
          }
        }
        encryptedKeyForPayload = Object.keys(encryptedKeyFor).length > 0 ? encryptedKeyFor : null;
        const encryptedResult = await encryption.encryptFileWithRawKey(fileBuffer, fileKeyBytes);
        if (!encryptedResult) throw new Error('Failed to encrypt file');
        const encryptedBlob = new Blob([encryptedResult.encryptedData], { type: 'application/octet-stream' });
        const encryptedName = `${selectedFile.name}.enc`;
        formData.append('file', encryptedBlob, encryptedName);
        formData.append('encrypted', 'true');
        formData.append('iv', encryptedResult.iv);
        formData.append('originalName', selectedFile.name);
        formData.append('originalMimeType', selectedFile.type || 'application/octet-stream');
        if (encryptedKeyForPayload) {
          formData.append('encryptedKeyFor', JSON.stringify(encryptedKeyForPayload));
        }
      } else {
        formData.append('file', selectedFile);
      }
      formData.append('chatId', chatId);
      formData.append('senderId', userId);

      const uploadResponse = await fetch('/api/chat/upload-file', {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        let errorMsg = t("chat.window.failedToUploadFile", "Failed to upload file");
        try {
          const uploadError = await uploadResponse.json();
          errorMsg = uploadError.error || errorMsg;
        } catch { errorMsg = t("chat.window.uploadFailedStatus", "Upload failed ({{status}})", { status: uploadResponse.status }); }
        throw new Error(errorMsg);
      }

      let uploadData;
      try { uploadData = await uploadResponse.json(); }
      catch { throw new Error(t("chat.window.invalidServerResponse", "Invalid response from server")); }
      setUploadProgress(50);

      const sendResponse = await fetch('/api/chat/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId,
          senderId: userId,
          text: newMessage.trim() || '',
          senderRole: userRole,
          attachment: {
            ...uploadData.file,
            encryptedKeyFor: encryptedKeyForPayload || undefined,
          },
          senderDeviceId: deviceId || undefined
        })
      });

      let sendData;
      try { sendData = await sendResponse.json(); }
      catch { throw new Error(t("chat.window.invalidServerResponse", "Invalid response from server")); }
      setUploadProgress(100);

      if (!sendResponse.ok) throw new Error(sendData.error || t("chat.window.failedToSendMessage", "Failed to send message"));

      setSelectedFile(null);
      setNewMessage('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Error uploading/sending file:', err);
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (msg) => {
    if (!msg.attachment) return;
    try {
      const response = await fetch('/api/chat/download-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId,
          messageId: msg.id,
          fileKey: msg.attachment.fileKey,
          userId,
          deleteAfterDownload: false,
          proxy: Boolean(msg.attachment.encrypted),
          fileName: msg.attachment.originalName || msg.attachment.fileName
        })
      });

      if (!response.ok) {
        if (response.status === 410) { setError(t("chat.window.fileExpired", "File has expired or been deleted")); return; }
        let errorMsg = t("chat.window.failedToDownloadFile", "Failed to download file");
        try { const error = await response.json(); errorMsg = error.error || errorMsg; }
        catch { errorMsg = t("chat.window.downloadFailedStatus", "Download failed ({{status}})", { status: response.status }); }
        throw new Error(errorMsg);
      }

      let data;
      try { data = await response.json(); }
      catch { throw new Error(t("chat.window.invalidServerResponse", "Invalid response from server")); }

      const confirmRemoteDeletion = async () => {
        if (!window.confirm(t("chat.window.confirmDownloadedFileSaved", "Confirm the file is saved on this device. The server copy will then be permanently deleted."))) return;
        const confirmResponse = await fetch('/api/chat/download-file', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId, messageId: msg.id, fileKey: msg.attachment.fileKey, userId, confirmDownload: true })
        });
        if (!confirmResponse.ok) throw new Error(t("chat.window.failedDeleteDownloadedFile", "File downloaded, but its remote copy could not be deleted"));
      };

      if (msg.attachment.encrypted) {
        if (!encryption.state.isInitialized) {
          await encryption.initializeEncryption(chatId, chatInfo?.peerPublicKey || null);
        }

        let encryptedBuffer;
        if (data.downloadBase64) {
          const binary = atob(data.downloadBase64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
          encryptedBuffer = bytes.buffer;
        } else {
          const fileResponse = await fetch(data.downloadUrl);
          encryptedBuffer = await fileResponse.arrayBuffer();
        }

        let senderKeys = chatInfo?.encryptionPublicKeys?.[msg.senderId];
        const peerKeys = chatInfo?.peerPublicKeys;
        const isSender = msg.senderId === userId;

        if (msg.senderDeviceId && (!senderKeys || (typeof senderKeys === 'object' && !senderKeys[msg.senderDeviceId]))) {
          try {
            const { getDoc, doc: docRef } = await import('firebase/firestore');
            const chatDoc = await getDoc(docRef(db, 'chats', chatId));
            if (chatDoc.exists()) {
              const freshSenderKeys = chatDoc.data()?.encryption?.publicKeys?.[msg.senderId];
              if (freshSenderKeys && typeof freshSenderKeys === 'object') senderKeys = freshSenderKeys;
            }
          } catch (err) { /* fallback */ }
        }

        const senderPublicKey = typeof senderKeys === 'string'
          ? senderKeys
          : (msg.senderDeviceId && senderKeys ? senderKeys[msg.senderDeviceId] : null) ||
            (senderKeys ? Object.values(senderKeys)[0] : null);
        const peerPublicKey = typeof peerKeys === 'string'
          ? peerKeys
          : (peerKeys ? Object.values(peerKeys)[0] : null);
        const decryptKey = isSender ? peerPublicKey : senderPublicKey;
        const encryptedKeyFor = msg.attachment.encryptedKeyFor;
        let decrypted = null;

        if (encryptedKeyFor && senderPublicKey) {
          const keyPayload = (deviceId && encryptedKeyFor[deviceId]) || encryptedKeyFor.default;
          if (keyPayload) {
            try {
              const fileKeyBase64 = await encryption.decryptFromPeer(senderPublicKey, keyPayload);
              if (fileKeyBase64 && !fileKeyBase64.includes('Encrypted') && !fileKeyBase64.includes('Failed')) {
                const keyBinary = atob(fileKeyBase64);
                const keyBytes = new Uint8Array(keyBinary.length);
                for (let i = 0; i < keyBinary.length; i += 1) keyBytes[i] = keyBinary.charCodeAt(i);
                decrypted = await encryption.decryptFileWithRawKey(encryptedBuffer, msg.attachment.iv, keyBytes);
              }
            } catch (e) { /* try fallback */ }
          }
          if (!decrypted) {
            for (const [keyId, payload] of Object.entries(encryptedKeyFor)) {
              if (keyId === deviceId || keyId === 'default') continue;
              try {
                const fileKeyBase64 = await encryption.decryptFromPeer(senderPublicKey, payload);
                if (fileKeyBase64 && !fileKeyBase64.includes('Encrypted') && !fileKeyBase64.includes('Failed')) {
                  const keyBinary = atob(fileKeyBase64);
                  const keyBytes = new Uint8Array(keyBinary.length);
                  for (let i = 0; i < keyBinary.length; i += 1) keyBytes[i] = keyBinary.charCodeAt(i);
                  decrypted = await encryption.decryptFileWithRawKey(encryptedBuffer, msg.attachment.iv, keyBytes);
                  if (decrypted) break;
                }
              } catch (e) { /* next */ }
            }
          }
        }
        if (!decrypted) {
          decrypted = decryptKey
            ? await encryption.decryptFileFromPeer(decryptKey, encryptedBuffer, msg.attachment.iv)
            : await encryption.decryptFileData(encryptedBuffer, msg.attachment.iv);
        }
        if (!decrypted && senderKeys && typeof senderKeys === 'object') {
          const candidates = Object.values(senderKeys);
          for (const candidate of candidates) {
            decrypted = await encryption.decryptFileFromPeer(candidate, encryptedBuffer, msg.attachment.iv);
            if (decrypted) break;
          }
        }
        if (!decrypted) { setError(t("chat.window.failedToDecryptFile", "Failed to decrypt file")); return; }

        const blob = new Blob([decrypted], { type: msg.attachment.originalMimeType || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = msg.attachment.originalName || msg.attachment.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        await confirmRemoteDeletion();
        return;
      }

      let blob;
      let shouldRevoke = false;
      if (data.downloadBase64) {
        const binary = atob(data.downloadBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], {
          type: data.contentType || msg.attachment.mimeType || 'application/octet-stream',
        });
        shouldRevoke = true;
      } else {
        blob = null;
      }
      const url = blob ? window.URL.createObjectURL(blob) : data.downloadUrl;
      const link = document.createElement('a');
      link.href = url;
      link.download = msg.attachment.originalName || msg.attachment.fileName || `attachment-${Date.now()}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (shouldRevoke) window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      await confirmRemoteDeletion();
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(err.message);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (selectedFile) { await uploadAndSendFile(); return; }
    if (!newMessage.trim() || sending) return;

    setSending(true);
    setError(null);

    try {
      // Client-side block check for seekers
      if (isQuester && chatInfo?.expertId) {
        try {
          const blocked = await isBlockedByMaven(chatInfo.expertId, userId);
          if (blocked) {
            setError(t("chat.window.mavenUnavailable", "This maven is not available for chat at this time"));
            setSending(false);
            return;
          }
        } catch (e) {
          // Fail-open — server-side check is authoritative
        }
      }
      const requestBody = {
        chatId,
        senderId: userId,
        text: newMessage.trim(),
        senderRole: userRole,
        senderDeviceId: deviceId || undefined
      };

      const peerKeys = chatInfo?.peerPublicKeys || {};
      const peerKeyEntries = Object.entries(peerKeys);
      let selfKeys = chatInfo?.encryptionPublicKeys?.[userId] || {};
      if (userId) {
        try {
          const { getDoc, doc: docRef } = await import('firebase/firestore');
          const chatDoc = await getDoc(docRef(db, 'chats', chatId));
          if (chatDoc.exists()) {
            const allEncryptionKeys = chatDoc.data()?.encryption?.publicKeys;
            const freshSelfKeys = allEncryptionKeys?.[userId];
            if (freshSelfKeys && typeof freshSelfKeys === 'object') selfKeys = freshSelfKeys;
          }
        } catch { /* fallback */ }
      }
      const selfKeyEntries = typeof selfKeys === 'object' ? Object.entries(selfKeys) : [];
      if (peerKeyEntries.length > 0 && encryption.publicKey) {
        const encryptedFor = {};
        for (const [peerDeviceId, peerPublicKey] of peerKeyEntries) {
          const encrypted = await encryption.encryptForPeer(peerPublicKey, newMessage.trim());
          if (encrypted?.ciphertext && encrypted?.iv) encryptedFor[peerDeviceId] = encrypted;
        }
        for (const [selfDeviceId, selfPublicKey] of selfKeyEntries) {
          if (encryptedFor[selfDeviceId]) continue;
          const encrypted = await encryption.encryptForPeer(selfPublicKey, newMessage.trim());
          if (encrypted?.ciphertext && encrypted?.iv) encryptedFor[selfDeviceId] = encrypted;
        }
        if (deviceId && encryption.publicKey) {
          if (!encryptedFor[deviceId]) {
            const selfEncrypted = await encryption.encryptForPeer(encryption.publicKey, newMessage.trim());
            if (selfEncrypted?.ciphertext && selfEncrypted?.iv) encryptedFor[deviceId] = selfEncrypted;
          }
        }
        if (Object.keys(encryptedFor).length > 0) {
          requestBody.text = '';
          requestBody.encryptedFor = encryptedFor;
          requestBody.isEncrypted = true;
        }
      } else if (encryption.state.hasSharedKey && chatInfo?.peerPublicKey) {
        // Only use the shared-key path when the peer has a CURRENT published key.
        // Without this guard, a stale shared key (peer reset encryption, or is on a
        // different device that never published to this chat) would encrypt a
        // message the recipient can't decrypt → "🔒 Encrypted message". If the peer
        // has no current key we fall through and send PLAINTEXT so it stays readable.
        const encrypted = await encryption.encryptMessage(newMessage.trim());
        if (encrypted) {
          requestBody.text = '';
          requestBody.encrypted = encrypted;
          requestBody.isEncrypted = true;
        }
      }

      const response = await fetch('/api/chat/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      let data;
      try { data = await response.json(); }
      catch { throw new Error(t("chat.window.invalidServerResponse", "Invalid response from server")); }

      if (!response.ok) {
        if (data.code === 'INSUFFICIENT_FUNDS') {
          setError(t("chat.window.insufficientBalance", "Insufficient balance. Need ${{required}}, have ${{available}}", { required: data.required.toFixed(2), available: data.available.toFixed(2) }));
        } else {
          setError(data.error || t("chat.window.failedToSendMessage", "Failed to send message"));
        }
        return;
      }

      // Update expert rate from API response
      if (chatInfo && data.expertRate) {
        setChatInfo(prev => ({
          ...prev,
          expertRate: data.expertRate
        }));
      }

      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t("chat.window.failedToSendMessage", "Failed to send message"));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Derive payer/earner by ID, not the (sometimes hardcoded) userRole prop: a
  // maven who INITIATED a chat sits in the questerId slot and is the payer even
  // though their account role is 'expert'. Fall back to role until chatInfo loads.
  const isQuester = chatInfo?.questerId
    ? userId === chatInfo.questerId
    : (userRole === 'quester' || userRole === 'sales' || userRole === 'seeker');
  const peerProfileId = chatInfo
    ? (chatInfo.questerId === userId ? chatInfo.expertId : chatInfo.questerId)
    : null;
  const myKeys = userId ? chatInfo?.encryptionPublicKeys?.[userId] : null;
  const hasDeviceKey = Boolean(
    deviceId && myKeys && (typeof myKeys === 'string' || (typeof myKeys === 'object' && myKeys[deviceId]))
  );
  const showKeyHint = Boolean(deviceId && chatInfo?.encryptionEnabled && !hasDeviceKey);

  const generateSummary = async (forceRegenerate = false) => {
    setSummaryLoading(true);
    setSummaryError(null);
    setShowSummary(true);
    try {
      const response = await fetch('/api/chat/summarize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, userId, forceRegenerate })
      });
      let data;
      try { data = await response.json(); }
      catch { throw new Error(t("chat.window.invalidServerResponse", "Invalid response from server")); }
      if (!response.ok) throw new Error(data.error || t("chat.window.failedToGenerateSummary", "Failed to generate summary"));
      setSummary(data.summary);
    } catch (err) {
      console.error('Error generating summary:', err);
      setSummaryError(err.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const copySummary = () => {
    if (!summary) return;
    const noneLabel = t("chat.window.none", "None");
    const text = `${t("chat.window.clipboardTitle", "Chat Summary")}\n\n${summary.overview}\n\n${t("chat.window.clipboardKeyTopics", "Key Topics:")}\n${summary.keyTopics?.map(topic => `- ${topic}`).join('\n') || noneLabel}\n\n${t("chat.window.clipboardActionItems", "Action Items:")}\n${summary.actionItems?.map(a => `- ${a}`).join('\n') || noneLabel}\n\n${t("chat.window.clipboardDecisions", "Decisions:")}\n${summary.decisions?.map(d => `- ${d}`).join('\n') || noneLabel}`;
    navigator.clipboard.writeText(text);
    alert(t("chat.window.summaryCopied", "Summary copied to clipboard!"));
  };

  const resetEncryption = async () => {
    if (!chatId || !userId) return;
    const confirmReset = window.confirm(t("chat.window.resetEncryptionConfirm", "Reset encryption for this chat? This will regenerate keys and require the other user to reopen the chat."));
    if (!confirmReset) return;
    try {
      window.localStorage.removeItem(`cmv_chat_encryption_${chatId}`);
      const chatRef = doc(db, 'chats', chatId);
      if (deviceId) {
        const existingKeys = chatInfo?.encryptionPublicKeys?.[userId];
        if (typeof existingKeys === 'string') {
          await updateDoc(chatRef, { [`encryption.publicKeys.${userId}`]: deleteField() });
        } else {
          await updateDoc(chatRef, { [`encryption.publicKeys.${userId}.${deviceId}`]: deleteField() });
        }
      }
      setEncryptionInitialized(false);
      decryptedCacheRef.current.clear();
      const newPublicKey = await encryption.initializeEncryption(chatId, chatInfo?.peerPublicKey || null);
      if (newPublicKey && deviceId) {
        await updateDoc(doc(db, 'chats', chatId), {
          [`encryption.publicKeys.${userId}.${deviceId}`]: newPublicKey,
          'encryption.enabled': true,
          'encryption.version': 5,
        });
      }
      setEncryptionInitialized(true);
      alert(t("chat.window.encryptionReset", "Encryption keys have been reset. Ask the other user to reopen the chat."));
    } catch (err) {
      console.error('[Chat] Failed to reset encryption:', err);
      alert(t("chat.window.failedToResetEncryption", "Failed to reset encryption. Please try again."));
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
        <div>
          {peerProfileId ? (
            <button
              type="button"
              onClick={() => router.push(`/expert-profile/${peerProfileId}`)}
              className="font-semibold text-left hover:underline focus:outline-none focus:underline"
              title={t("chat.window.viewProfile", "View profile")}
            >
              {(isQuester ? chatInfo?.expertName : chatInfo?.questerName) || t("chat.window.mavenFallback", "Maven")}
            </button>
          ) : (
            <h3 className="font-semibold">{chatInfo?.questerName}</h3>
          )}
          <p className="text-xs text-emerald-200">
            {t("chat.window.ratePerMessage", "${{rate}}/message", { rate: (chatInfo?.expertRate / 10).toFixed(2) })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* maven↔maven block — available to BOTH participants. Blocks the OTHER
              party (the seeker blocks the expert-maven, or vice versa). */}
          {(() => {
            const peerId = isQuester ? chatInfo?.expertId : chatInfo?.questerId;
            const peerName = (isQuester ? chatInfo?.expertName : chatInfo?.questerName) || t("chat.window.thisMaven", "this maven");
            if (!peerId || peerId === userId) return null;
            return (
              <button
                onClick={() => {
                  if (!window.confirm(t("chat.window.blockPeerConfirm", "Block {{name}}? They will no longer be able to call, message, or book you.", { name: peerName }))) return;
                  blockSeeker({
                    mavenId: userId,
                    seekerId: peerId,
                    seekerName: peerName,
                    seekerPhotoURL: '',
                  }).then(() => {
                    alert(t("chat.window.peerBlocked", "{{name}} has been blocked.", { name: peerName }));
                  }).catch((err) => {
                    console.error('Error blocking:', err);
                    alert(t("chat.window.failedToBlockPeer", "Failed to block. Please try again."));
                  });
                }}
                className="text-white hover:text-red-200 p-2 rounded-full hover:bg-red-500/40 transition-colors"
                title={t("chat.window.blockThisPerson", "Block")}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </button>
            );
          })()}
          <button onClick={resetEncryption} className="text-white hover:text-emerald-200 p-2 rounded-full hover:bg-emerald-500 transition-colors" title={t("chat.window.resetEncryption", "Reset encryption")}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v3m0 10v3m8-8h-3M7 12H4m11.364-4.364l-2.121 2.121M8.757 15.243l-2.121 2.121m0-7.485l2.121 2.121m6.486 6.486l2.121 2.121" />
            </svg>
          </button>
          <button onClick={clearChatHistoryForMe} className="text-white hover:text-emerald-200 p-2 rounded-full hover:bg-emerald-500 transition-colors" title={t("chat.window.clearChatHistory", "Clear chat history")}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-3h4m-6 3h8m-9 0h10" />
            </svg>
          </button>
          {onClose && (
            <button onClick={onClose} className="text-white hover:text-emerald-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Free-tier status + maven charge toggle */}
      {chatInfo && (() => {
        const charging = chatInfo.chargingEnabled !== false;
        // Free-message allowance is 5 platform-wide. Cap the displayed limit at
        // 5 so chats created before the 10→5 change (which stored 10) still show
        // the enforced value (the server counts against 5, not the stored field).
        const freeLimit = Math.min(chatInfo.freeMessageLimit || 5, 5);
        const freeLeft = Math.max(0, freeLimit - (chatInfo.freeMessagesUsed || 0));
        const perMsg = Math.max(0.01, (Number(chatInfo.expertRate) || 0) / 10).toFixed(2);
        return (
          <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 text-xs flex items-center justify-between gap-2">
            {chatInfo.freeOnlyUntilApproved ? (
              <span className="text-emerald-800 text-xs">
                {freeLeft > 0
                  ? `${freeLeft} of ${freeLimit} shared free messages left. Calls and paid chat unlock after this Maven completes their profile.`
                  : 'The 5 shared free messages are used. Complete and submit the profile to continue chatting.'}
              </span>
            ) : !isQuester ? (
              <>
                <span className="text-emerald-900">
                  {charging
                    ? t("chat.window.chargeOnHint", "First {{n}} messages are free, then you earn ${{rate}}/message.", { n: freeLimit, rate: perMsg })
                    : t("chat.window.chargeOffHint", "This chat is free — you won't earn from messages.")}
                </span>
                <button
                  onClick={async () => {
                    try { await updateDoc(doc(db, 'chats', chatId), { chargingEnabled: !charging }); }
                    catch (e) { console.error('Failed to toggle charging:', e); }
                  }}
                  className={`shrink-0 px-2.5 py-1 rounded-full font-semibold transition-colors ${charging ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  title={t("chat.window.toggleChargingTitle", "Turn charging on or off for this chat")}
                >
                  {charging ? t("chat.window.chargingOn", "Charging ON") : t("chat.window.chargingOff", "Charging OFF")}
                </button>
              </>
            ) : (
              <span className="text-emerald-900">
                {!charging
                  ? t("chat.window.seekerFree", "This chat is free 🎉")
                  : freeLeft > 0
                    ? t("chat.window.seekerFreeLeft", "{{n}} free messages left, then ${{rate}}/message", { n: freeLeft, rate: perMsg })
                    : t("chat.window.seekerPaid", "${{rate}}/message", { rate: perMsg })}
              </span>
            )}
          </div>
        );
      })()}

      {showKeyHint && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-900">
          {t("chat.window.keyHint", "This device has not finished registering encryption keys. Older messages may stay encrypted until it syncs.")}
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        onScroll={(e) => {
          const el = e.target;
          const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
          isUserScrollingRef.current = !isNearBottom;
        }}
      >
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>{t("chat.window.noMessagesYet", "No messages yet")}</p>
            <p className="text-sm">{t("chat.window.startConversation", "Start the conversation!")}</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.senderId === userId ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-lg px-4 py-2 ${msg.senderId === userId ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                {msg.attachment && (
                  <div className={`mb-2 p-2 rounded-lg ${msg.senderId === userId ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                    <div className="flex items-center gap-2">
                      <svg className="w-8 h-8 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{msg.attachment.fileName}</p>
                        <p className={`text-xs ${msg.senderId === userId ? 'text-emerald-200' : 'text-gray-500'}`}>
                          {(msg.attachment.fileSize / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      {msg.attachment.deleted ? (
                        <span className={`text-xs ${msg.senderId === userId ? 'text-emerald-200' : 'text-gray-500'}`}>{t("chat.window.expired", "Expired")}</span>
                      ) : (
                        <button onClick={() => handleDownload(msg)} className={`p-2 rounded-full hover:bg-opacity-20 hover:bg-black transition ${msg.senderId === userId ? 'text-white' : 'text-emerald-600'}`} title={t("chat.window.download", "Download")}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {msg.text && (
                  <TranslatableMessage
                    text={msg.text}
                    isOwn={msg.senderId === userId}
                    targetLang={viewerLang}
                    className="whitespace-pre-wrap break-words"
                  />
                )}
                <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${msg.senderId === userId ? 'text-emerald-200' : 'text-gray-400'}`}>
                  <span>{formatTime(msg.createdAt)}</span>
                  {!msg.isFree && msg.cost > 0 && <span className="ml-1">(${msg.cost.toFixed(2)})</span>}
                  <button onClick={() => deleteMessageForMe(msg)} className={`ml-2 underline ${msg.senderId === userId ? 'text-emerald-100' : 'text-gray-500'}`} title={t("chat.window.deleteForMe", "Delete for me")}>{t("chat.window.delete", "Delete")}</button>
                  {msg.senderId !== userId && (
                    <button onClick={() => reportMessage(msg)} className="ml-2 underline text-gray-500 hover:text-red-500" title={t("chat.window.reportMessage", "Report message")}>{t("chat.window.report", "Report")}</button>
                  )}
                  {msg.senderId === userId && msg.read && (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="bg-red-50 border-t border-red-200 px-4 py-2 text-sm text-red-600">
          {error}
          {error.includes('Insufficient') && <a href="/settings/payment" className="ml-2 underline font-medium">{t("chat.window.addPaymentMethod", "Add payment method")}</a>}
        </div>
      )}

      {selectedFile && (
        <div className="border-t border-b bg-gray-50 px-4 py-2">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
            </div>
            <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="p-1 text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {uploading && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="bg-emerald-600 h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-1">{t("chat.window.uploadingProgress", "Uploading... {{progress}}%", { progress: uploadProgress })}</p>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSend} className="border-t p-4">
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.doc,.docx" />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={sending || uploading} className="p-2 text-gray-400 hover:text-emerald-600 disabled:opacity-50 transition-colors" title={t("chat.window.attachFile", "Attach file (max 10MB)")}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>
          <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder={selectedFile ? t("chat.window.addMessageOptional", "Add a message (optional)...") : t("chat.window.typeMessage", "Type a message...")} className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent" disabled={sending || uploading} />
          <button type="submit" disabled={(sending || uploading) || (!newMessage.trim() && !selectedFile)} className="bg-emerald-600 text-white p-2 rounded-full hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {(sending || uploading) ? (
              <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1 text-center">{t("chat.window.filesExpire", "Files expire 5 min after download")}</p>
      </form>

      {/* Summary Modal */}
      {showSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h3 className="font-semibold">{t("chat.window.aiChatSummary", "AI Chat Summary")}</h3>
              </div>
              <button onClick={() => setShowSummary(false)} className="text-white hover:text-emerald-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {summaryLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <svg className="w-10 h-10 animate-spin text-emerald-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <p className="mt-3 text-gray-600">{t("chat.window.generatingSummary", "Generating summary...")}</p>
                </div>
              ) : summaryError ? (
                <div className="text-center py-8">
                  <p className="text-red-600">{summaryError}</p>
                  <button onClick={() => generateSummary(true)} className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">{t("chat.window.tryAgain", "Try Again")}</button>
                </div>
              ) : summary ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{t("chat.window.overview", "Overview")}</h4>
                    <p className="text-gray-700">{summary.overview}</p>
                  </div>
                  {summary.keyTopics?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t("chat.window.keyTopics", "Key Topics")}</h4>
                      <div className="flex flex-wrap gap-2">
                        {summary.keyTopics.map((topic, idx) => (
                          <span key={idx} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">{topic}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {summary.actionItems?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t("chat.window.actionItems", "Action Items")}</h4>
                      <ul className="space-y-1">
                        {summary.actionItems.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-700"><span className="text-emerald-600 mt-0.5">-</span>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {summary.decisions?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t("chat.window.decisionsMade", "Decisions Made")}</h4>
                      <ul className="space-y-1">
                        {summary.decisions.map((decision, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-700"><span className="text-green-600 mt-0.5">-</span>{decision}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="text-xs text-gray-400 pt-2 border-t">{t("chat.window.basedOnMessages", "Based on {{count}} messages", { count: summary.messageCount })}</div>
                </div>
              ) : null}
            </div>
            {summary && !summaryLoading && (
              <div className="border-t px-4 py-3 flex items-center justify-between bg-gray-50">
                <button onClick={() => generateSummary(true)} className="text-sm text-emerald-600 hover:text-emerald-800">{t("chat.window.regenerate", "Regenerate")}</button>
                <button onClick={copySummary} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {t("chat.window.copy", "Copy")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
